// src/pages/PublicSector/PublicSector.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://myboq.in";
const WEBSITE_NAME = process.env.REACT_APP_WEBSITE_NAME || "myBOQ";

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const PublicSector = ({ openContactModal }) => {
  useEffect(() => {
    track("public_sector_page_view", { path: "/public-sector" });
  }, []);

  const requestDemo = (placement = "public_sector") => {
    track("gov_demo_request_click", { placement, intent: "public_sector" });
    if (openContactModal) openContactModal();
  };

  return (
    <main className="bg-white">
      <SEO
        title={`${WEBSITE_NAME} | Public Sector Overview`}
        description="Public-sector ready SSR estimation & verification-friendly outputs for faster planning, tendering, approvals, and audits. Built for MH PWD SSR today."
        keywords="myBOQ public sector, MH PWD SSR, government estimation software, audit ready reports, tendering, compliance, standardization"
        image={`${BASE_URL}/ssrlogo3.png`}
        url={`${BASE_URL}/public-sector`}
      />

      {/* HERO (Institutions-style) */}
      <section className="px-5 sm:px-6 lg:px-20 pt-10 pb-10">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-500">
            <Link
              to="/"
              className="hover:text-orange-600"
              onClick={() =>
                track("nav_click", {
                  item: "home",
                  from: "public_sector_breadcrumb",
                })
              }
            >
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700 font-semibold">Public Sector</span>
          </div>

          <div className="mt-4 grid lg:grid-cols-12 gap-10 items-start">
            {/* Left */}
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
                Public Sector Standardization for Estimates & Approvals
              </h1>

              <p className="mt-4 text-gray-700 text-base sm:text-lg leading-relaxed">
                myBOQ helps departments, approval teams, and infrastructure
                consultants standardize estimation workflows and generate{" "}
                <span className="font-semibold text-gray-900">
                  verification-friendly, audit-ready
                </span>{" "}
                outputs — so planning, tendering, and reviews become faster and
                more consistent.
              </p>

              <p className="mt-3 text-gray-600">
                Built for Maharashtra today (MH PWD SSR), with a roadmap to
                expand into more departments and states.
              </p>

              {/* CTAs (same style as Institutions) */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => requestDemo("public_sector_hero")}
                  className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
                >
                  Request Department Demo
                </button>

                <Link
                  to="/public-sector/use-cases"
                  className="inline-flex items-center justify-center rounded-full bg-gray-100 px-6 py-3 text-gray-900 font-semibold hover:bg-gray-200 transition"
                  onClick={() =>
                    track("nav_click", {
                      item: "public_sector_use_cases",
                      placement: "public_sector_hero",
                    })
                  }
                >
                  View Use Cases
                </Link>
              </div>

              {/* Trust chips (same style as Institutions) */}
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  "Standard formats for review",
                  "Audit-ready outputs",
                  "Revision history",
                  "Verification-friendly structure",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="text-xs sm:text-sm px-3 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-100"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            {/* Right card (Institutions-style) */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                    <span className="text-xl" aria-hidden="true">
                      🏛️
                    </span>
                  </div>

                  <div>
                    <div className="text-gray-900 font-extrabold text-lg">
                      What departments get
                    </div>
                    <p className="text-gray-600 text-sm mt-1">
                      A consistent estimation workflow that reduces manual
                      checking, improves compliance, and speeds up approvals.
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {[
                    "Standard report structure",
                    "Approval-friendly recap/abstract",
                    "Revision & change tracking",
                    "Record-ready exports",
                  ].map((item) => (
                    <div
                      key={item}
                      className="rounded-xl border border-gray-200 p-3 bg-gray-50"
                    >
                      <span className="text-gray-900 font-semibold">✔</span>{" "}
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 text-xs text-gray-500">
                  Note: Currently optimized for MH PWD SSR; multi-department &
                  multi-state roadmap is planned.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Standardization (Institutions section header format) */}
      <section className="px-5 sm:px-6 lg:px-20 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
              Standardization that reduces verification time
            </h2>
            <div className="text-sm text-gray-500">
              Consistent structure improves approvals & audit readiness
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-white/70 border border-orange-100 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    ✅
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">
                    Verification-friendly workflows
                  </h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Reduce manual checks using consistent estimate structure,
                    recap/abstract views, and standardized outputs.
                  </p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Standard item grouping & SSR alignment</li>
                <li>Recap/abstract for faster validation</li>
                <li>Repeatable format across projects</li>
                <li>Fewer errors during review cycles</li>
              </ul>

              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="font-extrabold text-orange-700">
                  Faster approvals & fewer reworks
                </div>
                <Link
                  to="/public-sector/compliance"
                  className="inline-flex items-center justify-center rounded-full bg-orange-500 px-5 py-2 text-white text-sm font-semibold hover:bg-orange-600 transition"
                  onClick={() =>
                    track("nav_click", {
                      item: "public_sector_compliance",
                      placement: "public_sector_standardization",
                    })
                  }
                >
                  View Compliance
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <span className="text-xl" aria-hidden="true">
                    📑
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">
                    Audit-ready outputs & records
                  </h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Keep estimates, revisions, and exports organized for long-term
                    reference and audit trails.
                  </p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Consistent report formats</li>
                <li>Revision history support</li>
                <li>Export-ready documentation</li>
                <li>Record-keeping for future reference</li>
              </ul>

              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="font-extrabold text-gray-900">
                  Designed for audits
                </div>
                <button
                  type="button"
                  onClick={() => requestDemo("public_sector_outputs")}
                  className="inline-flex items-center justify-center rounded-full bg-gray-900 px-5 py-2 text-white text-sm font-semibold hover:bg-black transition"
                >
                  Request Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUTPUTS (Institutions-style cards) */}
      <section className="px-5 sm:px-6 lg:px-20 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
              Outputs designed for approvals, tendering, and audits
            </h2>

            <p className="mt-3 text-gray-600 leading-relaxed">
              Generate standardized outputs so stakeholders review faster and the
              same structure is used across projects.
            </p>

            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
              {[
                "PWD Estimate",
                "Schedule B",
                "Abstract",
                "Recap / General Abstract",
                "Rate Analysis",
                "Testing / compliance sections (as configured)",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-xl border border-gray-200 p-3 bg-gray-50"
                >
                  ✔ {item}
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/public-sector/use-cases"
                className="inline-flex items-center justify-center rounded-full bg-orange-500 px-5 py-2 text-white text-sm font-semibold hover:bg-orange-600 transition"
                onClick={() =>
                  track("nav_click", {
                    item: "public_sector_use_cases",
                    placement: "public_sector_outputs",
                  })
                }
              >
                Explore Use Cases
              </Link>

              <Link
                to="/public-sector/institutions"
                className="inline-flex items-center justify-center rounded-full bg-gray-100 px-5 py-2 text-gray-900 text-sm font-semibold hover:bg-gray-200 transition"
                onClick={() =>
                  track("nav_click", {
                    item: "public_sector_institutions",
                    placement: "public_sector_outputs",
                  })
                }
              >
                For Institutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom dark section (same vibe as Institutions) */}
      <section className="px-5 sm:px-6 lg:px-20 pb-14">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl bg-[#0B1B3A] text-white p-7 sm:p-10 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-extrabold">
              Public Sector Adoption Model
            </h2>

            <p className="mt-3 text-white/80 leading-relaxed">
              myBOQ can be adopted by departments and institutions to standardize
              estimation workflows and outputs — improving speed, accuracy, and
              compliance.
            </p>

            <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                Standardize estimate preparation
              </div>
              <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                Reduce approval cycle time
              </div>
              <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                Maintain audit-ready records
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2 text-sm">
                <span aria-hidden="true">✅</span>
                Prepare → Verify → Export → Approve
              </div>

              <div className="text-sm text-orange-300 font-semibold">
                Maharashtra focused today • Multi-state roadmap coming soon
              </div>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => requestDemo("public_sector_footer")}
                className="inline-flex items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-white font-semibold hover:bg-orange-600 transition"
              >
                Request Department Demo
              </button>

              <Link
                to="/public-sector/compliance"
                className="inline-flex items-center justify-center rounded-full bg-white/10 px-6 py-3 text-white font-semibold hover:bg-white/20 transition"
                onClick={() =>
                  track("nav_click", {
                    item: "public_sector_compliance",
                    placement: "public_sector_footer",
                  })
                }
              >
                View Compliance & Standardization →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PublicSector;